import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, Container, Row, Col } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import { graphConfig, loginRequest } from "authConfig";
import { scopes } from "apiurl";

const toProfileData = (graphUser = {}, fallbackAccount = null, storedProfile = null) => {
  const firstName =
    graphUser.givenName ||
    storedProfile?.firstName ||
    fallbackAccount?.idTokenClaims?.given_name ||
    "-";
  const lastName =
    graphUser.surname ||
    storedProfile?.lastName ||
    fallbackAccount?.idTokenClaims?.family_name ||
    "-";

  const fullName =
    graphUser.displayName ||
    storedProfile?.displayName ||
    [firstName, lastName].filter(Boolean).join(" ").trim() ||
    fallbackAccount?.name ||
    "Guest";

  return {
    fullName,
    firstName,
    lastName,
    email:
      graphUser.mail ||
      graphUser.userPrincipalName ||
      storedProfile?.email ||
      fallbackAccount?.username ||
      "-",
    jobTitle:
      graphUser.jobTitle ||
      storedProfile?.jobTitle ||
      fallbackAccount?.idTokenClaims?.jobTitle ||
      "-",
    department:
      graphUser.department || storedProfile?.department || "-",
    location:
      graphUser.officeLocation ||
      storedProfile?.location ||
      fallbackAccount?.idTokenClaims?.location ||
      "-",
    mobilePhone:
      graphUser.mobilePhone ||
      storedProfile?.mobilePhone ||
      fallbackAccount?.idTokenClaims?.phone_number ||
      "-",
    businessPhone:
      Array.isArray(graphUser.businessPhones) && graphUser.businessPhones.length > 0
        ? graphUser.businessPhones[0]
        : storedProfile?.businessPhone || "-",
  };
};

function UserProfile() {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];

  const [profileData, setProfileData] = useState(() => {
    const stored = sessionStorage.getItem("userInfo");
    const storedProfile = stored ? JSON.parse(stored) : null;
    return toProfileData({}, account, storedProfile);
  });

  useEffect(() => {
    const fetchGraphProfile = async () => {
      if (!account) {
        return;
      }

      const stored = sessionStorage.getItem("userInfo");
      const storedProfile = stored ? JSON.parse(stored) : null;

      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
          scopes,
        });

        const graphResponse = await fetch(graphConfig.graphMeEndpoint, {
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        });

        if (!graphResponse.ok) {
          throw new Error(`Graph profile request failed with status ${graphResponse.status}`);
        }

        const graphUser = await graphResponse.json();
        const mappedProfile = toProfileData(graphUser, account, storedProfile);
        setProfileData(mappedProfile);
        sessionStorage.setItem("userInfo", JSON.stringify(mappedProfile));
      } catch (error) {
        const fallbackProfile = toProfileData({}, account, storedProfile);
        setProfileData(fallbackProfile);
        sessionStorage.setItem("userInfo", JSON.stringify(fallbackProfile));
      }
    };

    fetchGraphProfile();
  }, [account, instance]);

  const displayName = useMemo(() => {
    if (!profileData?.fullName) {
      return "Guest";
    }
    return profileData.fullName.charAt(0).toUpperCase() + profileData.fullName.slice(1);
  }, [profileData]);

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col xs={12} lg={8} xl={7}>
          <Card className="shadow-sm border-0 overflow-hidden">
            <div style={{ position: "relative", height: 180 }}>
              <img
                alt="cover"
                src={require("assets/img/sidebar-2.jpg")}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "brightness(0.85)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.55))",
                }}
              />
            </div>

            <Card.Body className="pt-0">
              <Row className="align-items-end">
                <Col xs="auto">
                  <div
                    style={{
                      width: 92,
                      height: 92,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "4px solid #fff",
                      marginTop: -46,
                      boxShadow: "0 10px 20px rgba(0,0,0,0.15)",
                      background: "#fff",
                    }}
                  >
                    <img
                      alt="avatar"
                      src={require("assets/img/face-0.jpg")}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                </Col>

                <Col>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
                        {displayName}
                      </div>
                      <div style={{ opacity: 0.75, marginTop: 4 }}>{profileData.email}</div>
                    </div>
                  </div>

                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <Badge bg="secondary" className="px-3 py-2">
                      {profileData.jobTitle}
                    </Badge>
                    <Badge bg="warning" text="dark" className="px-3 py-2">
                      {profileData.location}
                    </Badge>
                  </div>
                </Col>
              </Row>

              <hr className="my-4" />

              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>FIRST NAME</div>
                  <div style={{ fontWeight: 600 }}>{profileData.firstName}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>LAST NAME</div>
                  <div style={{ fontWeight: 600 }}>{profileData.lastName}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>EMAIL</div>
                  <div style={{ fontWeight: 600 }}>{profileData.email}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>JOB TITLE</div>
                  <div style={{ fontWeight: 600 }}>{profileData.jobTitle}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>DEPARTMENT</div>
                  <div style={{ fontWeight: 600 }}>{profileData.department}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>LOCATION</div>
                  <div style={{ fontWeight: 600 }}>{profileData.location}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>MOBILE PHONE</div>
                  <div style={{ fontWeight: 600 }}>{profileData.mobilePhone}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>BUSINESS PHONE</div>
                  <div style={{ fontWeight: 600 }}>{profileData.businessPhone}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default UserProfile;
